import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Alert,
  Container,
  Form,
  Input,
  Label,
  FormFeedback,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import logo from '../../assets/images/NGK-LOGO.png';
import CarouselPage from '../AuthenticationInner/CarouselPage';
import {
  clearPendingTotp,
  getPendingTotp,
  verifyTotp,
} from '../../helpers/wordpress_totp';
import { clearPendingEmailOtp } from '../../helpers/wordpress_auth';
import { loginSuccess } from '../../store/actions';

const TotpVerify = () => {
  document.title = 'Authenticator Verification | NGK Tours';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');

  const pending = useMemo(() => getPendingTotp(), []);

  useEffect(() => {
    if (!pending?.challengeToken || pending.mode !== 'verify') {
      navigate('/login', { replace: true });
      return;
    }
    setChallengeToken(pending.challengeToken);
  }, [pending, navigate]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string().required('Enter your authenticator or recovery code'),
    }),
    onSubmit: async (values) => {
      setError(null);
      setSubmitting(true);
      try {
        const authData = await verifyTotp(challengeToken, values.code.trim());
        dispatch(loginSuccess(authData));
        navigate('/dashboard');
      } catch (err: any) {
        setError(err?.message || 'Verification failed.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <React.Fragment>
      <div className="auth-page">
        <Container fluid className="p-0">
          <Row className="g-0">
            <Col lg={4} md={5} className="col-xxl-3">
              <div className="auth-full-page-content d-flex p-sm-5 p-4">
                <div className="w-100">
                  <div className="d-flex flex-column h-100">
                    <div className="mb-4 mb-md-5 text-center">
                      <Link to="/login" className="d-block auth-logo">
                        <img src={logo} alt="NGK Tours" height="28" />
                      </Link>
                    </div>
                    <div className="auth-content my-auto">
                      <div className="text-center mb-4">
                        <div className="avatar-lg mx-auto">
                          <div className="avatar-title rounded-circle bg-light">
                            <i className="bx bx-shield-quarter h2 mb-0 text-primary"></i>
                          </div>
                        </div>
                        <h4 className="mt-4">Authenticator code</h4>
                        <p className="text-muted">
                          Enter the 6-digit code from your authenticator app,
                          or a recovery code.
                        </p>
                      </div>

                      {error && <Alert color="danger">{error}</Alert>}

                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label className="form-label">Verification code</Label>
                          <Input
                            name="code"
                            type="text"
                            autoComplete="one-time-code"
                            placeholder="123456 or recovery code"
                            value={validation.values.code}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(validation.touched.code && validation.errors.code)
                            }
                          />
                          {validation.touched.code && validation.errors.code ? (
                            <FormFeedback type="invalid">
                              {validation.errors.code}
                            </FormFeedback>
                          ) : null}
                        </div>
                        <div className="d-grid">
                          <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={submitting || !challengeToken}
                          >
                            {submitting ? 'Verifying...' : 'Verify & continue'}
                          </button>
                        </div>
                      </Form>

                      <div className="mt-4 text-center">
                        <Link
                          to="/login"
                          onClick={() => {
                            clearPendingTotp();
                            clearPendingEmailOtp();
                          }}
                        >
                          Back to login
                        </Link>
                        <p className="text-muted mt-3 mb-0 small">
                          Lost your device? Ask a Super Admin to reset your 2FA.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <CarouselPage />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TotpVerify;
